import { formatDateNoTime } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React, { useCallback, useState } from "react";
import { handleEvent } from "util/user-events";
import {
    ActionFailureMessage,
    describeForwardingAsk,
    ForwardingAction,
    isFailedFor,
    isRunningFor,
    LeadSourceForwardingConfirmation,
    LeadSourceForwardingConfirmationPanel,
    useForwardingAction,
} from "./lead-source-forwarding-confirmation";

export interface LeadSourceForwardingRequest extends LeadSourceForwardingConfirmation {
    notifiedAt?: string | null;
}

const MARK_APPROVED = "Mark as approved";
const MARK_FAILED_MESSAGE =
    "The forwarding could not be marked as approved. Please try again.";

const describeRelay = (request: LeadSourceForwardingRequest): string => {
    const received = `Received ${formatDateNoTime(request.receivedAt)}.`;
    if (request.notifiedAt) {
        return received;
    }
    return `${received} ${request.requesterAddress} could not be notified.`;
};

interface LeadSourceForwardingRequestRowProps {
    request: LeadSourceForwardingRequest;
    action: ForwardingAction<LeadSourceForwardingRequest>;
    onOpen: (request: LeadSourceForwardingRequest) => void;
}

const LeadSourceForwardingRequestRow: React.FC<
    LeadSourceForwardingRequestRowProps
> = props => {
    const { request, action, onOpen } = props;
    const open = useCallback(() => onOpen(request), [onOpen, request]);
    const mark = useCallback(() => action.run(request), [action, request]);

    return (
        <li className="space-y-2 py-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1 basis-60">
                    <p className="break-words text-sm text-yellow-900">
                        <span className="font-medium">{request.requesterAddress}</span>{" "}
                        {describeForwardingAsk(request)}
                    </p>
                    <p className="break-words text-xs text-yellow-700">
                        {describeRelay(request)}
                    </p>
                </div>
                <div className="flex shrink-0 gap-2">
                    <PrimaryCompactButton type="button" onClick={open}>
                        Open email
                    </PrimaryCompactButton>
                    <SecondaryCompactButton
                        type="button"
                        disabled={isRunningFor(action.state, request.id)}
                        onClick={mark}>
                        {MARK_APPROVED}
                    </SecondaryCompactButton>
                </div>
            </div>
            {isFailedFor(action.state, request.id) ? (
                <ActionFailureMessage message={MARK_FAILED_MESSAGE} />
            ) : null}
        </li>
    );
};

interface LeadSourceForwardingApprovalsProps {
    forwardingRequests: LeadSourceForwardingRequest[];
    onMarkApproved: (request: LeadSourceForwardingRequest) => Promise<void>;
}

export const LeadSourceForwardingApprovals: React.FC<
    LeadSourceForwardingApprovalsProps
> = props => {
    const { forwardingRequests, onMarkApproved } = props;
    const [openRequest, setOpenRequest] = useState<LeadSourceForwardingRequest | null>(
        null,
    );

    const open = useCallback((request: LeadSourceForwardingRequest) => {
        handleEvent("lead-source-forwarding-confirmation-open");
        setOpenRequest(request);
    }, []);
    const close = useCallback(() => setOpenRequest(null), []);

    const succeeded = useCallback((request: LeadSourceForwardingRequest) => {
        handleEvent("lead-source-forwarding-mark-approved");
        setOpenRequest(current => (current?.id === request.id ? null : current));
    }, []);
    const action = useForwardingAction(onMarkApproved, succeeded);
    const markOpenRequest = useCallback(
        () => (openRequest ? action.run(openRequest) : Promise.resolve()),
        [action, openRequest],
    );

    if (forwardingRequests.length === 0) {
        return null;
    }

    return (
        <div>
            <InlineBanner type="warning">
                <div className="space-y-2">
                    <div>
                        <p className="font-semibold text-yellow-900">
                            Approve mail forwarding
                        </p>
                        <p>
                            Forwarded mail will not reach this lead source until the
                            forwarding is approved. Open the confirmation email and follow
                            its instructions, then mark it as approved so this reminder
                            goes away.
                        </p>
                    </div>
                    <ul className="divide-y divide-yellow-200">
                        {forwardingRequests.map(request => (
                            <LeadSourceForwardingRequestRow
                                key={request.id}
                                request={request}
                                action={action}
                                onOpen={open}
                            />
                        ))}
                    </ul>
                </div>
            </InlineBanner>
            <LeadSourceForwardingConfirmationPanel
                request={openRequest}
                banner={
                    openRequest ? (
                        <InlineBanner type="info">
                            Follow the instructions in this email to approve forwarding
                            from {openRequest.requesterAddress}. Mail from that address
                            will not reach this lead source until the forwarding is
                            approved. Once you have approved it, mark it as approved to
                            clear the reminder.
                        </InlineBanner>
                    ) : null
                }
                action={
                    <SecondaryCompactButton
                        type="button"
                        disabled={
                            openRequest
                                ? isRunningFor(action.state, openRequest.id)
                                : false
                        }
                        onClick={markOpenRequest}>
                        {MARK_APPROVED}
                    </SecondaryCompactButton>
                }
                failure={
                    openRequest && isFailedFor(action.state, openRequest.id) ? (
                        <ActionFailureMessage message={MARK_FAILED_MESSAGE} />
                    ) : null
                }
                onDismiss={close}
            />
        </div>
    );
};
